import { SERVICES } from "@/constants/testIds/site";
import { Droplets, Building2, SquareStack, Sparkles } from "lucide-react";

const SHOWER_IMG =
  "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBnbGFzcyUyMHNob3dlciUyMG1vZGVybiUyMGJhdGhyb29tfGVufDB8fHx8MTc4MTM3NTIwOHww&ixlib=rb-4.1.0&q=85";
const CONSERV_IMG =
  "https://images.unsplash.com/photo-1758998202918-d921125a700f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&fit=crop";

export default function Services() {
  return (
    <section
      id="services"
      data-testid={SERVICES.section}
      className="relative py-24 md:py-32 px-6 md:px-10 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
              Services
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight max-w-3xl">
              We coat every kind of glass — and we do it where it lives.
            </h2>
          </div>
          <p className="md:max-w-sm text-slate-600 leading-relaxed">
            ClearShield can be applied to almost any glass surface. Here are
            the ones we touch most often.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Showers - large */}
          <div
            data-testid={SERVICES.showers}
            className="md:col-span-7 md:row-span-2 rounded-3xl overflow-hidden relative zoom-wrap hover-lift bg-emerald-950 group min-h-[420px]"
          >
            <img
              src={SHOWER_IMG}
              alt="Luxury glass shower"
              className="absolute inset-0 w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/85 via-emerald-950/30 to-transparent" />
            <div className="relative h-full p-8 flex flex-col justify-between text-white">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full glass-dark flex items-center justify-center">
                  <Droplets size={18} />
                </div>
                <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">
                  Most popular
                </div>
              </div>
              <div>
                <div className="font-display text-3xl md:text-4xl font-semibold">
                  Shower screens & enclosures
                </div>
                <div className="mt-3 max-w-md text-white/80 leading-relaxed">
                  Stop limescale before it starts. Water beads, soap residue
                  rinses away, and your shower looks new — for years.
                </div>
              </div>
            </div>
          </div>

          {/* Conservatories */}
          <div
            data-testid={SERVICES.conservatories}
            className="md:col-span-5 rounded-3xl overflow-hidden relative zoom-wrap hover-lift min-h-[200px]"
          >
            <img
              src={CONSERV_IMG}
              alt="Modern glass conservatory"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/70 to-transparent" />
            <div className="relative h-full p-7 flex flex-col justify-end text-white">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-9 h-9 rounded-full glass-dark flex items-center justify-center">
                  <Building2 size={16} />
                </div>
                <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">
                  Bifolds · Roof lights · Lanterns
                </div>
              </div>
              <div className="font-display text-2xl md:text-3xl font-semibold">
                Conservatories & glass extensions
              </div>
              <div className="mt-2 text-sm text-white/85 max-w-sm">
                Including bifold doors, roof lights and lanterns — we coat the lot.
              </div>
            </div>
          </div>

          {/* Windows */}
          <div
            data-testid={SERVICES.windows}
            className="md:col-span-3 rounded-3xl bg-white border border-slate-200 p-7 hover-lift flex flex-col justify-between min-h-[200px]"
          >
            <div className="w-11 h-11 rounded-full bg-emerald-100 flex items-center justify-center">
              <SquareStack size={18} className="text-emerald-700" />
            </div>
            <div>
              <div className="font-display text-2xl font-semibold text-emerald-950">
                Windows
              </div>
              <div className="text-sm text-slate-600 mt-2">
                Domestic & commercial. Stay cleaner, longer.
              </div>
            </div>
          </div>

          {/* Bespoke */}
          <div
            data-testid={SERVICES.bespoke}
            className="md:col-span-2 rounded-3xl bg-gradient-to-br from-emerald-600 to-emerald-800 text-white p-7 hover-lift flex flex-col justify-between min-h-[200px]"
          >
            <Sparkles size={20} />
            <div>
              <div className="font-display text-xl font-semibold">
                Bespoke glass
              </div>
              <div className="text-sm opacity-85 mt-1">
                Splashbacks, balustrades, atriums.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
